import { ReactBadge } from '@/components/react/ui/ReactBadge';
import { ReactImage } from '@/components/react/ui/ReactImage';
import type { ImageMetadata } from 'astro';
import { cx } from 'cva';
import range from 'lodash/range';
import sample from 'lodash/sample';
import type { HTMLAttributes } from 'react';
import React from 'react';
import invariant from 'tiny-invariant';

const TIMING = {
  MIN_SECONDS: 1,
  MAX_SECONDS: 5,
  INTERVAL: 1,
  ANIMATION: 1,
};

export interface InstagramItem extends HTMLAttributes<HTMLDivElement> {
  image: ImageMetadata;
  href?: string;
  handle: string;
  alt: string;
  startShown?: boolean;
  showAtSymbol?: boolean;
}

const InstagramCard: React.FC<InstagramItem> = ({
  image,
  handle,
  alt,
  href,
  showAtSymbol,
}) => {
  return (
    <div className="relative aspect-square h-full w-full">
      <a href={href}>
        <ReactImage
          className="relative h-full w-full rounded-lg object-cover"
          src={image.src}
          alt={alt}
          layout="fullWidth"
        />
        <div className="absolute inset-0 flex items-end p-2">
          <ReactBadge
            text={`${showAtSymbol ? '@' : ''}${handle}`}
            style="square"
            className="min-h-[24px] min-w-[63px] font-semibold"
            size="small"
          />
        </div>
      </a>
    </div>
  );
};

interface InstagramSlotProps {
  item: InstagramItem;
  maxDelay: number;
  shouldAnimate: boolean;
  showAtSymbol?: boolean;
}

const InstagramSlot: React.FC<InstagramSlotProps> = ({
  item,
  maxDelay,
  shouldAnimate,
  showAtSymbol = true,
}) => {
  const [show, setShow] = React.useState(item.startShown);

  React.useEffect(() => {
    if (show || !shouldAnimate) return;

    const possibleTimings = range(
      TIMING.MIN_SECONDS,
      maxDelay,
      TIMING.INTERVAL,
    );
    const randomTiming = sample(possibleTimings) || maxDelay;

    setTimeout(() => {
      setShow(true);
    }, randomTiming * 1000);
  }, [show, shouldAnimate, maxDelay]);

  return (
    <div
      style={{
        transitionDuration: `${TIMING.ANIMATION}s`,
        opacity: show ? 1 : 0,
      }}
      className="h-[155px] w-[155px] transition-opacity nth-of-type-[n+5]:hidden md:h-[218px] md:w-[218px] md:nth-of-type-[n+5]:block md:nth-of-type-[n+7]:hidden lg:h-auto lg:max-h-[193.33299px] lg:w-auto lg:max-w-[193.33299px] lg:nth-of-type-[n+7]:block"
    >
      <InstagramCard {...item} showAtSymbol={showAtSymbol} />
    </div>
  );
};

interface InstagramFeedProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'six' | 'twelve';
  items: InstagramItem[];
  showAtSymbol?: boolean;
}

export const InstagramFeed: React.FC<InstagramFeedProps> = ({
  className,
  items,
  size = 'six',
  showAtSymbol = true,
}) => {
  const maxItems = size === 'six' ? 6 : 12;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isIntersecting, setIntersecting] = React.useState(false);

  invariant(items.length >= maxItems, 'Not enough items defined for size');

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry?.isIntersecting && setIntersecting(true),
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const slots = React.useMemo<InstagramSlotProps[]>(
    () =>
      items.slice(0, maxItems).map((item, idx) => ({
        item,
        maxDelay: idx + TIMING.MAX_SECONDS,
        shouldAnimate: isIntersecting,
        showAtSymbol: showAtSymbol,
      })),
    [items, maxItems, isIntersecting, showAtSymbol],
  );

  return (
    <div
      ref={containerRef}
      className={cx(
        'grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6 lg:gap-6',
        className,
      )}
    >
      {slots.map((slot, idx) => {
        return <InstagramSlot key={idx} {...slot} />;
      })}
    </div>
  );
};
